(() => {
  const ROOT_SELECTOR = '[data-yb-ultra-k-redesign]';
  const MODULE_SELECTOR = '[data-yb-ultra-k-module]';
  const VIDEO_SELECTOR = '[data-yb-ultra-k-detail-video]';
  const REDUCED_MOTION_QUERY = '(prefers-reduced-motion: reduce)';

  const hydrateVideo = (video) => {
    const deferredSources = Array.from(video.querySelectorAll('source[data-src]'));
    if (deferredSources.length === 0) return;

    deferredSources.forEach((source) => {
      const sourceUrl = source.dataset.src;
      if (sourceUrl) source.src = sourceUrl;
      source.removeAttribute('data-src');
    });
    video.load();
  };

  const hydrateAndPauseVideo = (video) => {
    hydrateVideo(video);
    video.pause();
  };

  const pauseOtherVideos = (videos, activeVideo) => {
    videos.forEach((video) => {
      if (video !== activeVideo && !video.paused) video.pause();
    });
  };

  const safelyPlayVideo = (video, videos, reducedMotion) => {
    if (reducedMotion.matches) {
      video.pause();
      return;
    }

    hydrateVideo(video);
    pauseOtherVideos(videos, video);
    const playAttempt = video.play();
    if (playAttempt && typeof playAttempt.catch === 'function') {
      playAttempt.catch(() => video.pause());
    }
  };

  const initialiseModules = (root, reducedMotion) => {
    const modules = Array.from(root.querySelectorAll(MODULE_SELECTOR));
    if (reducedMotion.matches || typeof IntersectionObserver !== 'function') {
      modules.forEach((module) => module.classList.add('is-visible'));
      return;
    }

    const moduleObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-visible');
            moduleObserver.unobserve(entry.target);
          }
        });
      },
      { rootMargin: '0px 0px -8% 0px', threshold: 0.12 }
    );

    modules.forEach((module) => moduleObserver.observe(module));
  };

  const initialiseVideos = (root, reducedMotion) => {
    const videos = Array.from(root.querySelectorAll(VIDEO_SELECTOR));
    if (videos.length === 0) return;

    if (reducedMotion.matches || typeof IntersectionObserver !== 'function') {
      videos.forEach(hydrateAndPauseVideo);
      return;
    }

    const videoObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const video = entry.target;
          if (entry.isIntersecting && entry.intersectionRatio >= 0.3) {
            safelyPlayVideo(video, videos, reducedMotion);
          } else {
            video.pause();
          }
        });
      },
      { threshold: [0, 0.3, 0.75] }
    );

    videos.forEach((video) => videoObserver.observe(video));
  };

  const initialiseUltraKDetail = (root) => {
    if (!root || root.dataset.ybUltraKReady === 'true') return;

    root.dataset.ybUltraKReady = 'true';
    const reducedMotion = window.matchMedia(REDUCED_MOTION_QUERY);
    const videos = Array.from(root.querySelectorAll(VIDEO_SELECTOR));

    initialiseModules(root, reducedMotion);
    initialiseVideos(root, reducedMotion);

    const handleMotionChange = (event) => {
      if (event.matches) {
        videos.forEach(hydrateAndPauseVideo);
      }
    };

    if (typeof reducedMotion.addEventListener === 'function') {
      reducedMotion.addEventListener('change', handleMotionChange);
    } else {
      reducedMotion.addListener(handleMotionChange);
    }
  };

  const initialisePage = (scope = document) => {
    const root = scope.matches?.(ROOT_SELECTOR) ? scope : scope.querySelector(ROOT_SELECTOR);
    initialiseUltraKDetail(root);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initialisePage(), { once: true });
  } else {
    initialisePage();
  }

  document.addEventListener('shopify:section:load', (event) => initialisePage(event.target));
})();
